import {inject, Injectable, signal} from "@angular/core";
import Together from 'together-ai';
import {llmStoreService} from "@llm/service/llm_store.service";
import {ErrorHandlerService} from "@llm/service/error-handler.service";
import {environment} from "@env/environments.llm-dev";
import {tools_cadastro_cliente} from '@llm/models/schemas/agents/cadastro_client/call_tool_cadastro_cliente';
import {tools_cadastro_os} from '@llm/models/schemas/agents/cadastro_os/call_tool_cadastro_os';
import {tools_add_parts_os_budget} from '@llm/models/schemas/agents/agree_parts_to_os_budget/call_tool_add_parts_os_budget';
import {tools_search_web_info} from '@llm/models/schemas/agents/search_info/call_tool_search_web_info';
import {MainPrompt, MainSchema, AnalizerPrompt} from '@llm/models/schemas/agents/main_router/main_router';
import {ClientOptions} from "openai";
import {CompletionCreateParams} from 'together-ai/resources/chat/completions.mjs';
import {RegisterNewClientTool} from '@llm/models/schemas/agents/cadastro_client/tool_register_new_client.service';
import {RegisterNewOsTool} from '@llm/models/schemas/agents/cadastro_os/tool_register_new_os';
import {Tool_add_parts_os_budget} from '@llm/models/schemas/agents/agree_parts_to_os_budget/tool_add_parts_os_budget';
import {SearchWebInfoTool} from '@llm/models/schemas/agents/search_info/searchWebInfo.service';
import {ToolResponse} from '@llm/models/schemas/agents/tools_utils';
import {DataService} from '@serv/data.service';
import {Utiles_ferramentas} from '@llm/utils/utiles_ferramentas';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import {VEHICLE_PLATE_REGEX} from '@llm/utils/response_regex_for_streaming';

export type Role = 'user' | 'system' | 'assistant' | 'tool';

export type ToolCall = {
    id: string;
    type: 'function';
    function: {
        name: string;
        arguments: string;
    };
};

export type Message = {
    role: Role;
    content: string | null;
    tool_calls?: ToolCall[];
    tool_call_id?: string;
    // Propiedad interna para marcar eliminación segura
    _internal_id?: string;
};
/**
 * Servicio central para la inferencia y orquestación de interacciones con el Modelo de Lenguaje (LLM).
 * * Responsabilidades principales:
 * - Gestionar el ciclo de vida de los mensajes (envío, recepción, historial).
 * - Orquestar la ejecución de herramientas (Tools) definidas (registro de clientes, órdenes de servicio, búsqueda web).
 * - Implementar lógica de reintentos y recuperación de errores para llamadas al API del LLM.
 * - Mantener una "Memoria de Sesión" resumida para optimizar el contexto.
 * - Realizar "Poda Profunda" (Deep Pruning) del historial para eliminar transacciones completadas y ahorrar tokens.
 * - Validar y sanitizar el contenido de entrada y salida para prevenir inyecciones o contenido inseguro.
 */
@Injectable({ providedIn: 'root' })
export class LlmInferenceService {
    // #region Dependencies & Configuration
    private _store = inject(llmStoreService);
    private _errorHandlerService = inject(ErrorHandlerService);
    private _toolRegisterNewClient= inject(RegisterNewClientTool);
    private _toolRegisterNewOs= inject(RegisterNewOsTool);
    private _toolAddPartsOsBudget= inject(Tool_add_parts_os_budget);
    private _toolSearchWebInfo= inject(SearchWebInfoTool);
    private _dataService=inject(DataService);
    private _utils = inject(Utiles_ferramentas);
    private _console = inject(Tools_aiConsoleService);

    private readonly TRANSACTIONAL_TOOLS = ['createNewClient', 'createNewServiceOrder', 'addPartsToOsOrBudget'];
    private together_ai: Together;
    private together_ai_out_analizer: Together;
    private readonly llmModel = environment.TOGETHER_MODEL_RASONING_DEEP;

    private readonly requestTimeout = environment.API_TIMEOUT || 25000;

    private toolRegistry: Map<string, (args: any) => Promise<ToolResponse>>;
    private tools = [
        tools_cadastro_os[0],
        tools_cadastro_cliente[0],
        tools_add_parts_os_budget[0],
        tools_search_web_info[0]
    ];

    private _mainPrompt = MainPrompt;
    private systemMessage: Message;
    // #endregion

    // #region State Signals
    public messagesHistory = signal<Message[]>([]);
    // Agrega una propiedad para almacenar el resumen estructurado
    private sessionSummary: string[] = [];

    public actionRealiced = signal<Message>({
        role: 'assistant',
        content: 'History of actions performed during the session: #__#'
    });

    public tools_call_id_instant=signal<string | null>(null)
    public tools_call_search_web_markdown_info=signal<string | null>(null)
    public tools_call_search_web_name=signal<string | null>(null)
    // #endregion


    constructor() {
        const clientOptions: ClientOptions = {
            baseURL: environment.TOGETHER_API_URL,
            apiKey: environment.TOGETHER_API_KEY,
            dangerouslyAllowBrowser: true
        };
        this.together_ai = new Together(clientOptions);
        this.together_ai_out_analizer = new Together(clientOptions);

        this.systemMessage = {
            role: 'system',
            content: this.replacePlaceholders(this._mainPrompt),
        };

        this.toolRegistry = new Map();
        this.toolRegistry.set('createNewClient', async (args) => {
            const dataForAgent = { extracted_data: args.data };
            return await this._toolRegisterNewClient.processAgentData(dataForAgent);
        });
        this.toolRegistry.set('createNewServiceOrder', async (args) => {
            const dataForAgent = { extracted_data: args.data };
            return await this._toolRegisterNewOs.processAgentData(dataForAgent);
        });
        this.toolRegistry.set('addPartsToOsOrBudget', async (args) => {
            const dataForAgent = { args };
            console.info('[INFO:]' ,dataForAgent)
            return await this._toolAddPartsOsBudget.processAgentData(dataForAgent.args);
        });
        this.toolRegistry.set('google_web_search', async (args) => {
            this._store.action_instant.update(() => 'Pesquisando informações online')
            const query = args.query as string;
            const result = await this._toolSearchWebInfo.search(query);
            const clearResult =await this._utils.clearTextPortugues(result) + '#Important:All this information is rendered to the system user in markdown format. You should offer to clarify any doubts regarding the information or if they want to broaden the search.'
            this._store.action_instant.update(() => 'Respondendo a consulta ')
            return {
                status: 'ok',
                data: clearResult,
            } as ToolResponse;
        });
    }

    // #region Public API

    /**
     * Procesa el mensaje del usuario y gestiona el ciclo de vida de la interacción con el LLM.
     * Incluye lógica de reintentos, ejecución de herramientas, actualización de memoria y limpieza de contexto.
     * @param userMessage El mensaje enviado por el usuario.
     * @returns Una promesa con la respuesta final en formato texto.
     */
    async sendMessage(userMessage: Message): Promise<string> {
        const sanitizedUserMessage = this.sanitizeMessages([userMessage])[0];
        sanitizedUserMessage.content = this.annotateVehiclePlates(sanitizedUserMessage.content);
        const currentMessages = [...this.messagesHistory(), sanitizedUserMessage];
        this.messagesHistory.set(currentMessages);

        let max_iterations = 10;
        const successfulTransactionIds = new Set<string>();

        const memoryList = this.sessionSummary.length > 0
            ? this.sessionSummary.map((action, index) => `${index + 1}. ${action}`).join('\n')
            : 'No actions performed yet.';

        const now = new Date();
        const dateHour = now.toLocaleString();
        const dateHourISO = now.toISOString();
        const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
        const today = daysOfWeek[now.getDay()];


        const dynamicSystemMessage: Message = {
            role: 'system',
            content: `${this.replacePlaceholders(this._mainPrompt)}

                        # MEMORY SESSION #:
                        ${memoryList}

                        Note: Use this memory strictly to know which actions HAVE ALREADY been performed and their order.
                        The message history may have been cleared to save context.Now is ${today} in ${dateHour}`.replace(/[\s\n]+/g, ' ').trim()
        };


        try {
            while (max_iterations > 0) {
                max_iterations--;

                const completionParams: CompletionCreateParams = {
                    model: this.llmModel,
                    max_tokens: 20000,
                    messages: [dynamicSystemMessage, ...this.messagesHistory()] as any,
                    temperature: 0,
                    //tool_choice:"auto",
                    reasoning_effort: "high",
                    tools: this.tools,
                    stream: false,
                    reasoning: {"enabled":true}
                    //response_format: MainSchema
                };

                const completionParamsOutAnalizer: CompletionCreateParams = {
                    messages: [
                        {
                            role: "system",
                            content: AnalizerPrompt
                        }
                    ],
                    model: "meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8",
                    stream: false,
                    response_format: {
                        type: "json_object"
                    }
                };


                // Llamada al método encapsulado de reintentos
                let assistantMessage: any;
                try {
                    assistantMessage = await this.fetchCompletionWithRetries(completionParams, completionParamsOutAnalizer);
                } catch (e) {// Si fetchCompletionWithRetries falla tras 3 intentos, salimos del while principal
                    this.messagesHistory.set(currentMessages);
                    return 'Pedimos desculpas. Não foi possível processar sua solicitação após varias tentativas. Tente novamente.';
                }

                const assistantMessageTransformado = this.convertirContenidoNulo(assistantMessage);

                if ('reasoning' in assistantMessageTransformado) { //TODO: Parche
                    delete (assistantMessageTransformado as any).reasoning;
                }

                assistantMessageTransformado.content=this.extractFinalAnswer(assistantMessageTransformado.content || '')

                if(assistantMessage.tool_calls && assistantMessage.tool_calls.length > 0){
                    assistantMessageTransformado.tool_calls.forEach(tools_call=>{
                        try{
                            tools_call.function.arguments = JSON.stringify(JSON.parse(tools_call.function.arguments));

                        }catch (e) {
                            console.error("Error al parsear argumentos:", tools_call.function.arguments, e);
                        }

                    })

                }


                this.messagesHistory.update(history => [...history, assistantMessageTransformado as Message]);

                // --- PROCESAMIENTO DE TOOLS ---
                if (assistantMessage.tool_calls && assistantMessage.tool_calls.length > 0) {
                    let aggressiveCleanTriggered = false;

                    const toolOutputs = await Promise.all(assistantMessage.tool_calls.map(async (toolCall: any) => {
                        const toolResult = await this.executeTool(toolCall);

                        // --- LÓGICA DE MEMORIA Y LIMPIEZA ---
                        if (this.TRANSACTIONAL_TOOLS.includes(toolCall.function.name)) {
                            const isSuccess = toolResult.status === 'ok';

                            if (isSuccess) {
                                successfulTransactionIds.add(toolCall.id);
                                const summary = this.generateMemorySummary(
                                    toolCall.function.name,
                                    toolCall.function.arguments,
                                    toolResult
                                );
                                this.updateActionsRealiced(summary);
                            }
                        }
                        // -------------------------------------


                        const isDuplicateError = toolResult.status === 'error' && toolResult.reason?.includes('SYSTEM_ALERT');
                        return {
                            role: 'tool' as Role,
                            tool_call_id: toolCall.id,
                            content: JSON.stringify(toolResult),
                            _internal_is_duplicate_error: isDuplicateError
                        };
                    }));

                    // 2. Limpieza del historial
                    const hasSuccessOrNormalError = toolOutputs.some(out => !(out as any)._internal_is_duplicate_error);
                    if (hasSuccessOrNormalError) {
                        this.cleanHistoryOfDuplicateErrors();
                    }
                    toolOutputs.forEach(out => delete (out as any)._internal_is_duplicate_error);

                    this.messagesHistory.update(history => [...history, ...toolOutputs]);
                } else {

                    this._console.log('Final response from the model.');
                    const finalContent = this.extractFinalAnswer(assistantMessage.content || '');

                    // --- LIMPIEZA AGRESIVA POST-RESPUESTA ---
                    /*if (successfulTransactionIds.size > 0) {
                        // Si hubo transacciones exitosas en esta sesión de inferencia,
                        // y el modelo ya generó su respuesta final sobre ellas:
                        this._console.log('[Aggressive Clean] Transaction successful. Wiping message history AFTER final response.');

                        // Borramos TODO el historial de mensajes (User, Assistant, Tools).
                        // La "memoria" persiste en la variable `sessionSummary` que se inyecta en el System Message.
                        try{
                            this.messagesHistory.set([]);
                        }catch (e) {
                            this.pruneDeepHistory(successfulTransactionIds);
                        }


                        // Nota: Retornamos `finalContent` para que la UI lo muestre ahora,
                        // pero el historial queda limpio para la PRÓXIMA interacción.
                    } else {*/
                        // Comportamiento estándar si NO hubo transacciones críticas (solo charla o búsquedas)
                        this.cleanHistoryOfDuplicateErrors();

                        const currentHistory = this.messagesHistory();
                        const lastMsg = currentHistory[currentHistory.length - 1];

                        if (!lastMsg || lastMsg.content !== finalContent) {
                            this.messagesHistory.update(history => [...history, { role: 'assistant', content: finalContent }]);
                        }
                    //}

                    /*if(this._toolSearchWebInfo.tools_call_search_web_markdown_info()){
                        const data_pesquisa =this._utils.clearTextPortugues(this._toolSearchWebInfo.tools_call_search_web_markdown_info())
                        const toolCallId = await this.tools_call_id_instant();
                        if(toolCallId){
                            this.messagesHistory.update(history => history.map(msg =>{
                                if(msg.role =='tool' && msg.tool_call_id ==toolCallId){
                                    let existingData: any={};
                                    try{
                                        existingData = JSON.parse(msg.content || '{}');
                                    }catch {}
                                    if (existingData.data_pesquisa) {
                                        existingData.data_pesquisa += '\n\n' + data_pesquisa;
                                    } else {
                                        existingData.data_pesquisa = data_pesquisa;
                                    }
                                    return { ...msg, content: JSON.stringify(existingData) };
                                }
                                return msg;
                            })
                        )}

                    }*/
                    // if(this.messagesHistory()[this.messagesHistory().length-1].content !=finalContent){
                    //     this.messagesHistory.update(history => [...history, { role: 'assistant', content: finalContent }]);
                    // }

                    this._store.action_instant.update(()=>'')
                    return finalContent;
                }
            }

            return 'Desculpe, devido a um erro interno não consegui processar sua solicitação. Tente novamente em breve.';
        } catch (error) {
            console.error(error);
            this._errorHandlerService.handleError(error);
            this._store.action_instant.update(()=>'')
            return 'Error durante el procesamiento del mensaje.';
        }
    }

    /**
     * Resetea el historial de mensajes y la memoria de sesión.
     */
    clearHistory(): void {
        this.messagesHistory.set([]);
        this.sessionSummary = [];
        this.actionRealiced.set({
            role: 'assistant',
            content: 'History of actions performed during the session: #__#'
        });
    }

    /**
     * Registra una acción exitosa en el resumen de sesión y actualiza la señal observable.
     * @param actionDescription Descripción textual de la acción realizada.
     * @version modf_v2
     */
    updateActionsRealiced(actionDescription: string): void {
        const timestamp = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const logEntry = `[${timestamp}] SUCESSO: ${actionDescription}`;

        this.sessionSummary.push(logEntry);
        this.actionRealiced.update(act => ({
            ...act,
            content: `Session Summary Log:\n${this.sessionSummary.join('\n')}`
        }));
    }



    // #endregion

    // #region Core LLM Logic
    /**
     * Realiza una llamada a la API de generación de completions con una estrategia de reintentos robusta.
     *
     * La función implementa un bucle de reintentos para manejar:
     * - Errores de red o fallos en la llamada a la API.
     * - Respuestas vacías o incompletas del modelo.
     * - Alucinaciones en el contenido, intentando corregirlas mediante `recoverHallucinatedToolCalls`.
     * - Validación estricta de seguridad del contenido para evitar que se expongan estructuras internas,
     *   código o JSON contaminado al usuario.
     *
     * Estrategia:
     * - Se realizan hasta 3 intentos (configurable mediante `maxRetries`).
     * - En cada intento se espera medio segundo antes de reintentar si ocurre un error.
     * - Si se logra obtener un mensaje válido y seguro, se devuelve inmediatamente.
     * - Si se agotan los intentos, se lanza el último error capturado.
     *
     * @param {CompletionCreateParams} completionParams - Parámetros de configuración para la llamada
     *        a la API de completions. Incluye el prompt, opciones de generación y demás ajustes.
     *
     * @param completionParamsOutAnalizer
     * @returns {Promise<any>} - Promesa que se resuelve con el mensaje generado por el modelo
     *                           si la respuesta es válida y segura. Lanza un error si todos los
     *                           intentos fallan.
     *
     * @throws {Error} - Puede lanzar errores en los siguientes casos:
     * - Respuesta vacía del modelo.
     * - Contenido inseguro detectado (código o JSON expuesto).
     * - Fallos de red o errores internos tras agotar los reintentos.
     *
     * @example
     * // Uso básico con parámetros de completions
     * const params: CompletionCreateParams = {
     *   model: "together-ai-chat",
     *   messages: [{ role: "user", content: "Hola, ¿puedes ayudarme?" }]
     * };
     *
     * try {
     *   const result = await fetchCompletionWithRetries(params);
     *   console.log("Respuesta segura:", result);
     * } catch (error) {
     *   console.error("Fallo tras múltiples intentos:", error);
     * }
     *
     * @example
     * // Caso de contenido inseguro
     * const params: CompletionCreateParams = {
     *   model: "together-ai-chat",
     *   messages: [{ role: "user", content: "```console.log('Hola');```" }]
     * };
     *
     * try {
     *   const result = await fetchCompletionWithRetries(params);
     * } catch (error) {
     *   // → Lanza error por contenido inseguro
     * }
     */
    private async fetchCompletionWithRetries(completionParams: CompletionCreateParams, completionParamsOutAnalizer: CompletionCreateParams): Promise<any> {
        let attemptCount = 0;
        const maxRetries = 3;

        while (attemptCount < maxRetries) {
            attemptCount++;
            try {
                const response = await this.together_ai.chat.completions.create(completionParams);
                const msg = response?.choices?.[0]?.message;

                if (!msg) throw new Error("Respuesta vacía del modelo");

                if (msg.content || msg.tool_calls.length==0) {
                    this.recoverHallucinatedToolCalls(msg);
                }
                if (!msg.content && (!msg.tool_calls || msg.tool_calls.length === 0)) {
                    completionParamsOutAnalizer.messages.push({
                        role: "user",
                        content: `Analyze and reorganize this message if it is poorly formatted; otherwise, return it as is: ${JSON.stringify(msg)}`
                    })
                    let responseAnalizer = await this.together_ai_out_analizer.chat.completions.create(completionParamsOutAnalizer);
                    const msg2 = responseAnalizer?.choices?.[0]?.message;
                    if(msg2?.content){
                        try{
                            const parsedCorrection = JSON.parse(msg2.content);
                            msg.content = parsedCorrection.content || null;
                            msg.tool_calls = parsedCorrection.tool_calls || [];
                        }catch (e) {
                            console.error("Error parseando la corrección del modelo analizador", e);
                        }
                    }


                }

                const contentToCheck = msg.content || '';
                if (!this.isContentSafe(contentToCheck)) {
                    this._console.error(`Intento ${attemptCount}: Contenido contaminado con código/JSON detectado. Reintentando...`);
                    throw new Error("Contenido inseguro detectado (código o JSON expuesto al usuario)");
                }

                return msg;

            } catch (retryError) {
                this._console.error(`Error en intento ${attemptCount} de ${maxRetries}:`, retryError);
                if (attemptCount >= maxRetries) {
                    throw retryError;
                }
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        throw new Error("Error inesperado en el bucle de reintentos");
    }

    /**
     * Ejecuta una herramienta específica solicitada por el modelo, verificando duplicados antes de la ejecución.
     * @param toolCall Objeto que contiene el nombre y argumentos de la herramienta.
     * @returns La respuesta de la herramienta o un mensaje de error.
     */
    private async executeTool(toolCall: ToolCall): Promise<ToolResponse> {
        this._console.log(`Executing tool: ${toolCall.function.name} with arguments: ${toolCall.function.arguments}`);

        // --- VERIFICACIÓN DE SEGURIDAD CONTRA BUCLES / DATOS REPETIDOS ---
        //@DEPRECATED
        // if (this.isDuplicateToolCall(toolCall)) {
        //     const warningMsg = `SYSTEM_ALERT: We detected that you called the tool '${toolCall.function.name}' with EXACTLY the same arguments as in a previous successful execution.
        //     The user is probably requesting a NEW operation (e.g., a different client).
        //     Please review the user's last message, ignore the data from the previous execution, and generate NEW arguments based on the new request.`;
        //
        //     this._console.warn(`Duplicate tool call prevented: ${toolCall.function.name}`);
        //
        //     return {
        //         status: 'error',
        //         reason: warningMsg
        //     };
        // }

        try{
            const toolFunction = this.toolRegistry.get(toolCall.function.name);
            if (toolFunction) {
                const args = JSON.parse(toolCall.function.arguments);
                this.tools_call_id_instant.update(() => toolCall.id)
                return await toolFunction(args);
            }
            return { status: 'error', reason: 'Tool not found' };
        }catch (error) {
            this._console.error(`Error executing tool ${toolCall.function.name}:`, error);
            return { status: 'error', reason: `Error during tool execution: ${error.message}` };
        }

    }

    // #endregion

    // #region History Management

    /**
     * Realiza una poda profunda del historial eliminando todo el flujo de conversación (usuario/asistente)
     * que llevó a una transacción exitosa, preservando solo el resultado final.
     * @param successfulToolCallIds Conjunto de IDs de herramientas que se ejecutaron exitosamente.
     */
    private pruneDeepHistory(successfulToolCallIds: Set<string>): void {
        if (successfulToolCallIds.size === 0) return;

        this.messagesHistory.update(currentHistory => {
            let history = [...currentHistory];

            successfulToolCallIds.forEach(targetId => {

                const toolResultIndex = history.findIndex(m => m.role === 'tool' && m.tool_call_id === targetId);
                if (toolResultIndex === -1) return;

                let toolCallIndex = -1;
                for (let i = toolResultIndex - 1; i >= 0; i--) {
                    const msg = history[i];
                    if (msg.role === 'assistant' && msg.tool_calls?.some(tc => tc.id === targetId)) {
                        toolCallIndex = i;
                        break;
                    }
                }
                if (toolCallIndex === -1) return;

                let startIndexToDelete = toolCallIndex;

                for (let i = toolCallIndex - 1; i > 0; i--) {
                    const msg = history[i];

                    if (msg.role === 'tool') {
                        break;
                    }

                    startIndexToDelete = i;
                }

                const deleteCount = (toolResultIndex - startIndexToDelete) + 1;

                if (deleteCount > 0 && startIndexToDelete >= 0) {
                    this._console.log(`[Deep Prune] Cleaning transaction ${targetId}. Removing ${deleteCount} messages.`);
                    history.splice(startIndexToDelete, deleteCount);
                }
            });

            return history;
        });
    }

    /**
     * Limpia del historial los errores de ejecución generados por llamadas duplicadas.
     */
    private cleanHistoryOfDuplicateErrors(): void {
        this.messagesHistory.update(history => {
            const errorMarker = "SYSTEM_ALERT: We detected that you called the tool";

            const errorIndices = history
                .map((msg, index) => ({ msg, index }))
                .filter(({ msg }) => msg.role === 'tool' && msg.content?.includes(errorMarker))
                .map(({ index }) => index);

            if (errorIndices.length === 0) return history;

            const indicesToRemove = new Set<number>(errorIndices);
            const toolCallIdsToRemove = new Set<string>();

            errorIndices.forEach(idx => {
                const callId = history[idx].tool_call_id;
                if (callId) toolCallIdsToRemove.add(callId);
            });


            return history.reduce((acc, msg, index) => {
                if (indicesToRemove.has(index)) return acc;

                if (msg.role === 'assistant' && msg.tool_calls) {
                    const hasBadCall = msg.tool_calls.some(tc => toolCallIdsToRemove.has(tc.id));

                    if (hasBadCall) {
                        const newToolCalls = msg.tool_calls.filter(tc => !toolCallIdsToRemove.has(tc.id));
                        if (newToolCalls.length === 0 && (!msg.content || msg.content.trim() === '')) {
                            return acc;
                        }
                        return [...acc, { ...msg, tool_calls: newToolCalls }];
                    }
                }

                return [...acc, msg];
            }, [] as Message[]);
        });
    }

    /**
     * Genera una cadena de texto resumen basada en el resultado de una herramienta para almacenarla en memoria.
     * @param toolName Nombre de la herramienta ejecutada.
     * @param resultArgs Argumentos enviados a la herramienta.
     * @param resultOutput Resultado devuelto por la herramienta.
     */
    private generateMemorySummary(toolName: string, resultArgs: any, resultOutput: any): string {
        let args = resultArgs;
        try { if (typeof args === 'string') args = JSON.parse(args); } catch (e) {console.error('Error parsing args summary', e);}

        let output = resultOutput;
        try { if (typeof output === 'string') output = JSON.parse(output); } catch (e) { console.error('Error parsing output summary', e); }
        if (output && output.msg) {
            return `[Action Log] ${output.msg}`;
        }


        const params = args?.data || args || {};
        const responseData = output?.data || {};
        const responseId = responseData?.id || responseData?.os_id || responseData?.client_id || (typeof responseData === 'object' ? 'ID_DESCONHECIDO' : responseData);

        switch (toolName) {
            case 'createNewClient':
                const nome = params.name || 'Name N/A';
                const doc = params.document || 'Doc N/A';
                const telefone = params.phone ? `Tel: ${params.phone}` : '';
                const action = params.client_id ? 'updated':'registered';
                return `CLIENT ${action}: ${nome} (Doc: ${doc})  ${telefone} → ${responseData.reason}  [System ID: ${responseId}]`;

            case 'createNewServiceOrder':
                const tipo = params.isBudget ? 'Budget' : 'Service Order';
                const placa = params.plate || 'Plate N/A';
                const clienteRef = params.client || 'Client N/A';
                return `${tipo.toUpperCase()} successfully created. Plate: ${placa}. Client Ref: ${clienteRef} [System ID: ${responseId}]`;

            case 'addPartsToOsOrBudget':
                const partsCount = args?.parts?.length || 0;
                const osId = resultOutput?.data?.os_id || 'N/A';
                const osCode = resultOutput?.data?.os_code || 'N/A';
                const addedParts = resultOutput?.data?.added_parts?.join(', ') || 'nenhuma';
                const errors = resultOutput?.data?.error_parts?.join('; ') || '';

                if (resultOutput?.status === 'ok') {
                    return `PEÇAS ADICIONADAS: ${partsCount} peça(s) na OS ${osCode} [System ID: ${osId}]. Peças: ${addedParts}`;
                } else if (resultOutput?.status === 'pending') {
                    return `PEÇAS ADIÇÃO PARCIAL: ${partsCount} peça(s) solicitadas na OS ${osCode}. Sucesso: ${addedParts}. Erros: ${errors}`;
                } else {
                    return `PEÇAS FALHA: Erro ao adicionar peças na OS ${osCode}. Erro: ${errors}`;
                }

            default:
                return `Action executed: ${toolName}. Status: ${output?.status || 'unknown'}`;
        }
    }

    /**
     * Limpia propiedades internas como timestamp de los mensajes para enviarlos a la API.
     */
    private sanitizeMessages(messages: any[]): Message[] {
        return messages.map(({ timestamp, ...rest }) => rest);
    }
    // #endregion

    // #region Response Processing & Safety

    /**
     * Determina si un contenido textual es seguro para ser procesado o mostrado.
     *
     * La validación es estricta y busca evitar que el contenido incluya:
     * - Bloques de código en formato Markdown (```).
     * - Etiquetas XML/HTML residuales relacionadas con llamadas internas
     *   (ej. <tool_calls>, <function_calls>, <tool_result>, <json>, <function>).
     * - Estructuras JSON completas al inicio y final del texto que contengan
     *   claves típicas de llamadas a herramientas (name, arguments, parameters, tool).
     * - JSON embebido dentro del texto que simule llamadas a funciones o herramientas.
     *
     * @param {string} content - El contenido a validar. Puede ser cualquier texto
     *                           ingresado por el usuario o generado por el sistema.
     *
     * @returns {boolean} - Devuelve `true` si el contenido es considerado seguro,
     *                      `false` si se detecta código, JSON expuesto o estructuras internas.
     *
     * @example
     * // Caso seguro: texto plano
     * isContentSafe("Hola, ¿cómo estás?");
     * // → true
     *
     * @example
     * // Caso inseguro: bloque de código
     * isContentSafe("```console.log('Hola');```");
     * // → false
     *
     * @example
     * // Caso inseguro: estructura JSON sospechosa
     * isContentSafe('{"name":"tool","arguments":{"param":"valor"}}');
     * // → false
     *
     * @example
     * // Caso seguro: texto con llaves pero sin estructura JSON típica
     * isContentSafe("Este texto contiene {algo} pero no es JSON.");
     * // → true
     */
    private isContentSafe(content: string): boolean {
        if (!content || content.trim().length === 0) return true;
        if(!content || content.trim().length >= 2000 ) return true;
        if (/```/.test(content)) return false;
        if (/```/.test(content)) return false;
        if (/<(\/)?(tool_calls|function_calls|tool_result|json|function|'reasoning steps'|'here are my reasoning'|'use says'| 'they provide'| 'have a conversation' | 'mmmmm' )[^>]*>/i.test(content.toLowerCase())) return false;

        const trimmed = content.trim();

        const startsWithStructure = trimmed.startsWith('{') || trimmed.startsWith('[');
        const endsWithStructure = trimmed.endsWith('}') || trimmed.endsWith(']');

        if (startsWithStructure && endsWithStructure) {
            if (/["'](name|arguments|parameters|tool)["']\s*:/i.test(trimmed)) return false;
        }

        if (/["']name["']\s*:\s*["'].+["']\s*,\s*["']arguments["']/i.test(content)) return false;

        return true;
    }

    /**
     * Intenta recuperar llamadas a herramientas (ToolCalls) que fueron "alucinadas"
     * o incrustadas en texto plano dentro del contenido de un mensaje.
     *
     * El proceso consiste en:
     * - Detectar bloques XML con la etiqueta <tool_calls> en el contenido.
     * - Extraer y limpiar el JSON incrustado, corrigiendo problemas comunes
     *   como escapes Unicode inválidos o arrays incompletos.
     * - Parsear el JSON y convertirlo a un formato estándar de ToolCall.
     * - Inyectar las llamadas recuperadas en la propiedad `tool_calls` del mensaje.
     * - Limpiar el contenido original para eliminar el bloque <tool_calls>.
     *
     * @param {any} message - Objeto de mensaje que puede contener texto plano
     *                        con llamadas a herramientas incrustadas.
     *                        Se espera que tenga la propiedad `content` como string.
     *
     * @returns {void} - No devuelve valor. Modifica el objeto `message` directamente
     *                   agregando la propiedad `tool_calls` si se logra la recuperación.
     *
     * @example
     * // Caso: mensaje con bloque <tool_calls>
     * const msg = {
     *   content: "<tool_calls>[{\"name\":\"search\",\"arguments\":{\"query\":\"hola\"}}]</tool_calls>"
     * };
     * recoverHallucinatedToolCalls(msg);
     * // → msg.tool_calls contendrá un array con la llamada recuperada
     * // → msg.content quedará limpio sin el bloque <tool_calls>
     *
     * @example
     * // Caso: mensaje sin bloque <tool_calls>
     * const msg = { content: "Texto normal sin llamadas" };
     * recoverHallucinatedToolCalls(msg);
     * // → No se modifica el objeto, permanece igual
     */
    private _recoverHallucinatedToolCalls(message: any): void {
        if (!message.content || typeof message.content !== 'string') return;

        const regex = /<tool_calls>(.*?)<\/tool_calls>/s;
        const match = message.content.match(regex);

        if (match) {
            this._console.log('Detector de alucinaciones: Encontrada llamada a herramienta en texto plano. Intentando recuperar...');
            let rawJson = match[1];

            try {
                rawJson = rawJson.replace(/\\\\u([0-9a-fA-F]{4})/g, (match, p1) => {
                    const code = parseInt(p1, 16);
                    if (code === 34 || code === 92 || code < 32) {
                        return match;
                    }
                    return String.fromCharCode(code);
                });

                rawJson = rawJson.trim();
                if (rawJson.startsWith('[') && !rawJson.endsWith(']')) {
                    this._console.log('Detector de alucinaciones: Array JSON incompleto detectado, agregando cierre "]"');
                    rawJson += ']';
                }

                const parsedTools = JSON.parse(rawJson);

                if (Array.isArray(parsedTools)) {
                    const recoveredTools: ToolCall[] = parsedTools.map((t: any) => ({
                        id: `call_${Math.random().toString(36).substring(2, 10)}_${Date.now()}`,
                        type: 'function',
                        function: {
                            name: t.name,
                            arguments: typeof t.arguments === 'string' ? t.arguments : JSON.stringify(t.arguments)
                        }
                    }));

                    message.tool_calls = recoveredTools;

                    const contentWithoutTools = message.content.replace(match[0], '').trim();
                    message.content = contentWithoutTools === '' ? null : contentWithoutTools;

                    this._console.log('Detector de alucinaciones: Recuperación exitosa.', recoveredTools);
                }
            } catch (e) {
                this._console.error('Detector de alucinaciones: Fallo al parsear el JSON alucinado.', e);
            }
        }
    }


    /**
     * Recupera llamadas a herramientas desde XML alucinado O desde el formato nativo de DeepSeek.
     */
    private recoverHallucinatedToolCalls(message: any): void {
        const contentSources = [
            message.content,
            message.reasoning
        ].filter(text => typeof text === 'string' && text.length > 0);

        let recoveredTools: ToolCall[] = message.tool_calls || [];
        let cleanContent = message.content;

        const deepSeekRegex = /<｜tool▁call▁begin｜>([\s\S]*?)<｜tool▁sep｜>([\s\S]*?)<｜tool▁call▁end｜>/g;
        const xmlRegex = /<tool_calls>(.*?)<\/tool_calls>/s;

        contentSources.forEach(sourceText => {
            let match;
            while ((match = deepSeekRegex.exec(sourceText)) !== null) {
                const functionName = match[1].trim();
                const functionArgs = match[2].trim();

                this._console.log(`[DeepSeek Fix] Herramienta detectada en texto plano: ${functionName}`);

                recoveredTools.push({
                    id: `call_ds_${Math.random().toString(36).substring(2, 10)}`, // ID Generado
                    type: 'function',
                    function: {
                        name: functionName,
                        arguments: functionArgs
                    }
                });
                if (cleanContent && cleanContent.includes(match[0])) {
                    cleanContent = cleanContent.replace(match[0], '');
                }
            }

            // --- ESTRATEGIA B: XML Hallucination ---
            const xmlMatch = sourceText.match(xmlRegex);
            if (xmlMatch) {
                this._console.log('Detector de alucinaciones (XML): Intentando recuperar...');
                let rawJson = xmlMatch[1];
                try {
                    rawJson = rawJson.replace(/\\\\u([0-9a-fA-F]{4})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)));
                    rawJson = rawJson.trim();
                    if (rawJson.startsWith('[') && !rawJson.endsWith(']')) rawJson += ']';

                    const parsedTools = JSON.parse(rawJson);
                    if (Array.isArray(parsedTools)) {
                        const xmlTools = parsedTools.map((t: any) => ({
                            id: `call_xml_${Math.random().toString(36).substring(2, 10)}`,
                            type: 'function',
                            function: {
                                name: t.name,
                                arguments: typeof t.arguments === 'string' ? t.arguments : JSON.stringify(t.arguments)
                            }
                        }));
                        recoveredTools = [...recoveredTools, ...xmlTools] as any;

                        if (cleanContent) {
                            cleanContent = cleanContent.replace(xmlMatch[0], '');
                        }
                    }
                } catch (e) {
                    this._console.error('Fallo parsing XML tools', e);
                }
            }
        });

        if (recoveredTools.length > 0) {
            message.tool_calls = recoveredTools;
            cleanContent = cleanContent
                ?.replace(/<｜tool▁calls▁begin｜>/g, '')
                ?.replace(/<｜tool▁calls▁end｜>/g, '')
                ?.trim() || null;

            message.content = cleanContent;

            this._console.log(`[Recovery] Total tools recuperadas: ${recoveredTools.length}`);
        }
    }

    /**
     * Extrae la respuesta final limpiando marcadores internos del modelo.
     */
    private extractFinalAnswer(raw: string): string {
        if (!raw) return '';
        if (raw.includes('assistantfinal')) {
            return this._1extractFinalAnswer(raw);
        } else if (raw.includes('final<|message|>')) {
            return this._3extractFinalAnswer(raw);
        }
        // else if (raw.includes('final')) {
        //     return this._2extractFinalAnswer(raw);}
        else {
            return raw.trim();
        }
    }

    /**
     * Helper para manejar diferentes formatos de marcadores finales.
     */
    private _1extractFinalAnswer(raw: string): string {
        const marker = 'assistantfinal';
        const idx = raw.indexOf(marker);
        if (idx !== -1) {
            return raw.substring(idx + marker.length).trim();
        }
        return raw.trim();
    }

    /**
     * Helper para manejar diferentes formatos de marcadores finales.
     */
    private _2extractFinalAnswer(raw: string): string {
        const match = raw.match(/final(.*)$/i);
        return match ? match[1].trim() : raw.trim();
    }

    /**
     * Helper para manejar diferentes formatos de marcadores finales.
     */
    private _3extractFinalAnswer(raw: string): string {
        const marker = 'final<|message|>';
        const idx = raw.lastIndexOf(marker);
        if (idx !== -1) {
            return raw.substring(idx + marker.length).trim();
        }
        return raw.trim();
    }

    /**
     * Convierte contenido nulo o indefinido a string 'null'.
     */
    private convertirContenidoNulo(message: Message): Message {
        const nuevoMensaje = { ...message };

        if (nuevoMensaje.content === null || nuevoMensaje.content === undefined) {
            nuevoMensaje.content = 'null';
        }

        return nuevoMensaje;
    }

    /**
     * Verifica si una llamada a herramienta es idéntica a una anterior en el historial.
     */
    private isDuplicateToolCall(currentToolCall: ToolCall): boolean {
        const currentArgsNormalized = this.normalizeJsonString(currentToolCall.function.arguments);
        const currentName = currentToolCall.function.name;
        for (const msg of this.messagesHistory()) { // Recorremos el historial
            if (msg.role === 'assistant' && msg.tool_calls) {
                for (const pastCall of msg.tool_calls) {
                    if (pastCall.id === currentToolCall.id) continue; // Importante: No comparar la llamada actual consigo misma (usando el ID)

                    if (pastCall.function.name === currentName) {
                        const pastArgsNormalized = this.normalizeJsonString(pastCall.function.arguments);
                        if (pastArgsNormalized === currentArgsNormalized) { // Si nombre y argumentos coinciden exactamente
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    /**
     * Normaliza cadenas JSON para comparación, eliminando espacios irrelevantes.
     */
    private normalizeJsonString_origin(jsonStr: string): string {
        try {// Parsear y volver a stringify elimina espacios extra y ordena (si keys están en mismo orden)
            return JSON.stringify(JSON.parse(jsonStr));
        } catch {
            return jsonStr.replace(/\s/g, '');
        }
    }

    /**
     * Normaliza cadenas JSON para comparación, eliminando espacios irrelevantes y ordenando claves.
     * TODO: TESTE
     */
    private normalizeJsonString(jsonStr: string): string {
        try {
            const obj = JSON.parse(jsonStr);
            // Ordenar claves recursivamente para garantizar consistencia
            const sortKeys = (o: any): any => {
                if (typeof o !== 'object' || o === null) return o;
                if (Array.isArray(o)) return o.map(sortKeys);
                return Object.keys(o).sort().reduce((acc, key) => {
                    acc[key] = sortKeys(o[key]);
                    return acc;
                }, {} as any);
            };
            return JSON.stringify(sortKeys(obj));
        } catch {
            return jsonStr.replace(/\s/g, '');
        }
    }

    // #endregion

    // #region Text & Data Utilities

    /**
     * Reemplaza marcadores de posición en el prompt con valores reales de configuración.
     */
    private replacePlaceholders(content: string): string {
        let replacedContent = content;

        const replacements: Record<string, string> = {
            '_tools_ai_tom_': this._store.settingsToolsAi().tone,
            '_perzonalized_refer_by_user_': this._store.settingsToolsAi().username,
            '_name_empresa_':this._dataService.company.fancyName,
        };

        for (const placeholder in replacements) {
            if (Object.prototype.hasOwnProperty.call(replacements, placeholder)) {
                replacedContent = replacedContent.replace(
                    new RegExp(placeholder, 'g'),
                    replacements[placeholder]
                );
            }
        }

        return replacedContent;
    }

    /**
     * Añade notas al sistema si detecta patrones de matrículas de vehículos en el texto.
     * Ayuda al modelo a identificar correctamente placas en formatos comunes.
     */
    private annotateVehiclePlates(content: string | null): string | null {
        if (!content) return content;

        const matches = content.match(VEHICLE_PLATE_REGEX);

        if (matches && matches.length > 0) {
            const uniquePlates = Array.from(new Set(matches)).join(', ');
            return `${content} [System Note: The text pattern(s) ${uniquePlates} detected in this message correspond to vehicle license plates (placa/chapa).]`;
        }

        return content;
    }
    // #endregion

    /**
     * TODO: (UNUSED AND DEPRECATED)PALEATIVOS A TENER EN CUNETA CUANDO SE CAMBIE PARA EL BACKEND.
     */


    private safeParse(arg: any) {
        try {
            while (typeof arg === "string") {
                arg = JSON.parse(arg);
            }
            return arg;
        } catch {
            return arg; // si falla, devuelve el original
        }
    }



}
